import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TablerIconsModule } from 'angular-tabler-icons';

export interface SearchableSingleSelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-searchable-single-select',
  standalone: true,
  imports: [CommonModule, FormsModule, TablerIconsModule],
  templateUrl: './searchable-single-select.component.html',
  styleUrl: './searchable-single-select.component.scss',
})
export class SearchableSingleSelectComponent {
  private searchInput?: ElementRef<HTMLInputElement>;
  private shouldFocusSearchInput = false;
  private shouldIgnoreNextOptionClick = false;
  private shouldIgnoreNextClearClick = false;

  @ViewChild('searchInput')
  set searchInputRef(input: ElementRef<HTMLInputElement> | undefined) {
    this.searchInput = input;

    if (input && this.shouldFocusSearchInput) {
      this.focusSearchInput();
    }
  }

  @Input() options: SearchableSingleSelectOption[] = [];
  @Input() selectedValue: string | null = null;
  @Input() placeholder = 'Selecione uma opcao';
  @Input() searchPlaceholder = 'Buscar...';
  @Input() sectionTitle = 'Opcoes';
  @Input() showSectionTitle = true;
  @Input() emptyMessage = 'Nenhuma opcao encontrada.';
  @Input() selectedSummaryEmpty = 'Nenhuma opcao selecionada';
  @Input() clearLabel = 'Limpar';
  @Input() clearable = true;
  @Input() disabled = false;
  @Output() selectedValueChange = new EventEmitter<string | null>();

  isOpen = false;
  searchTerm = '';

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  get filteredOptions(): SearchableSingleSelectOption[] {
    const normalizedSearch = this.normalize(this.searchTerm);

    if (!normalizedSearch) {
      return this.options;
    }

    return this.options.filter((option) =>
      this.normalize(option.label).includes(normalizedSearch),
    );
  }

  get selectedOption(): SearchableSingleSelectOption | undefined {
    return this.options.find((option) => option.value === this.selectedValue);
  }

  get triggerLabel(): string {
    return this.selectedOption?.label ?? this.placeholder;
  }

  get footerLabel(): string {
    return this.selectedOption?.label ?? this.selectedSummaryEmpty;
  }

  toggleOpen(): void {
    if (this.disabled) return;

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.focusSearchInput();
    } else {
      this.shouldFocusSearchInput = false;
    }
  }

  isSelected(value: string): boolean {
    return this.selectedValue === value;
  }

  selectValueFromPointer(value: string, event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    this.shouldIgnoreNextOptionClick = true;
    this.selectValue(value, event);

    setTimeout(() => {
      this.shouldIgnoreNextOptionClick = false;
    });
  }

  selectValueFromClick(value: string, event: MouseEvent): void {
    if (this.shouldIgnoreNextOptionClick) {
      this.shouldIgnoreNextOptionClick = false;
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    this.selectValue(value, event);
  }

  selectOnlyFilteredOptionFromKeyboard(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.disabled || this.filteredOptions.length !== 1) {
      return;
    }

    this.selectValue(this.filteredOptions[0].value, event);
  }

  private selectValue(value: string, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (this.disabled) return;

    this.selectedValue = value;
    this.selectedValueChange.emit(value);
    this.isOpen = false;
    this.searchTerm = '';
    this.shouldFocusSearchInput = false;
  }

  clearSelectionFromPointer(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    this.shouldIgnoreNextClearClick = true;
    this.clearSelection(event);

    setTimeout(() => {
      this.shouldIgnoreNextClearClick = false;
    });
  }

  clearSelectionFromClick(event: MouseEvent): void {
    if (this.shouldIgnoreNextClearClick) {
      this.shouldIgnoreNextClearClick = false;
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    this.clearSelection(event);
  }

  private clearSelection(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (this.disabled || !this.clearable) return;

    this.selectedValue = null;
    this.selectedValueChange.emit(null);
    this.searchTerm = '';
    this.focusSearchInput();
  }

  private focusSearchInput(): void {
    this.shouldFocusSearchInput = true;

    setTimeout(() => {
      const input = this.searchInput?.nativeElement;

      if (!this.isOpen || !input) {
        return;
      }

      input.focus({ preventScroll: true });
      input.select();
      this.shouldFocusSearchInput = false;
    });
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
      this.shouldFocusSearchInput = false;
    }
  }

  private normalize(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
